import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  submitRVQuotation,
  RequestRVEO,
  FamilyGroup,
  Modality,
} from "../../Services/RVEOService";
import moment from "moment";
import { toast } from "react-toastify";
import {
  formatCurrencySummation,
  transformDataGF,
  transformDataMO,
} from "../../Utils/rveoUtils";
import { ExtraFormRV } from "../../types/formRVEO";
import { useConfigData } from "./useConfigData";
import { useFormNavigation } from "./useFormNavigation";
import { useBeneficiaries } from "./useBeneficiaries";
import { useModalities } from "./useModalities";
import { useFinancialData } from "./useFinancialData";
import { useHistory } from "react-router-dom";
import { Auth } from "aws-amplify";
import {
  RVEOCurrentUserInfo,
  RVEODataState,
  RVEOFormRequest,
} from "../../interfaces/rveoForm";
import { useSelector } from "react-redux";
import { decryptEstandar } from "../../Utils/CryptographyUtils";

/**
 * Hook principal para el formulario RVEO
 * Coordina múltiples hooks especializados para mantener la lógica organizada
 */
export const useFormRVEO = () => {
  // Datos de configuración
  const {
    isLoadingConfig,
    assured,
    afpData,
    coverageData,
    serviceData,
    saludData,
    genrerData,
    currencyData,
    modData,
    deferredYearsData,
    tempYearsData,
    guaranteedYearsData,
    relationshipData,
    stepsFormData,
  } = useConfigData();

  // Estados locales
  const [objSendForm, setObjSendForm] = useState<ExtraFormRV>();
  const [steps, setSteps] = useState<number>(1);
  const [city, setCity] = useState<string>("");
  const [selectAfp, setSelectAfp] = useState<any>();
  const [selectSalud, setSelectSalud] = useState<any>();
  const contentRef = useRef<any>(null);
  const history = useHistory();
  // Formulario principal de datos personales
  const methods = useForm({});
  const { handleSubmit, register, errors, watch, reset, setValue, getValues } =
    methods;

  const coverage = watch("coverage");
  const prestacionWatch = watch("prestacion");
  const afp = watch("afp");
  const cuspp = watch("cuspp");
  const averageRenum = watch("averageRenum");

  // Navegación
  const navigationProps = useFormNavigation(stepsFormData);
  const {
    containerFormRef,
    activeStep,
    setActiveStep,
    stepsForm,
    setStepsForm,
    showTitleActive,
    nextActiveStep,
    prevActiveStep,
  } = navigationProps;

  // Datos financieros
  const financialProps = useFinancialData(
    saludData,
    prestacionWatch,
    coverage,
    objSendForm
  );
  const {
    methodFinancial,
    handleSubmitFinancial,
    registerFinancial,
    setValueFinancial,
    cic,
    recogBonus,
    addContribution,
    capitalPension,
    dateDeven,
    dateDevOrig,
    saludWatch,
  } = financialProps;

  // Beneficiarios
  const beneficiariesProps = useBeneficiaries(
    relationshipData,
    setValue,
    assured && assured.sexo
  );
  const {
    parients,
    setParients,
    selectedParient,
    showModalAddBeneficiarie,
    setShowModalAddBeneficiarie,
    deleteParient,
    handleOpenModalAddBeneficiaries,
    handleCloseModalBeneficiarie,
    handleOpenModalEditBeneficiarie,
    handleAddBeneficiarie,
    handleSubmitBeneficiarie,
    registerBeneficiarie,
    errorsBeneficiarie,
    methodsBeneficiarie,
    relationshipBeneficiarie,
    bornDateBeneficiarie,
    saludBeneficiarie,
    genrerBenefiarie,
    setValueBeneficiarieLocal: setValueBeneficiarie,
    resetBeneficiaries,
    getFilteredRelationshipData,
  } = beneficiariesProps;

  // Modalidades
  const modalitiesProps = useModalities(
    modData,
    deferredYearsData,
    prestacionWatch
  );
  const {
    modalities,
    setModalities,
    selectedModalitie,
    showModalMod,
    setShowModalMod,
    modalitiesOptions,
    setModalitiesOptions,
    deferredYears,
    setDeferredYears,
    deleteModalitie,
    handleOpenModalAddModalities,
    handleCloseModalModalities,
    handleOpenModalEditModalitie,
    handleAddModalitie,
    handleSubmitModalitie,
    registerModalitie,
    errorsModalitie,
    resetModalities,
    modalitiesSelect,
    currencyMod,
    defYearsMod,
    tempYearsMod,
    guarYearsMod,
    setValueModalitie,
  } = modalitiesProps;

  const asesorSel = useSelector((state: any) =>
    decryptEstandar(state.login.idClient)
  );

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Funciones auxiliares

  const formatCurrency = (value: any) => {
    return new Intl.NumberFormat("es-PE", {
      style: "currency",
      currency: "PEN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const renderName = (valueP: string, arrEntry: any) => {
    const findElement = arrEntry.find(
      (element: any) => element.value === valueP
    );
    return findElement ? findElement.name : "";
  };

  const getNumberValue = (value: any) => {
    return typeof value === "object" && value !== null
      ? Number(value.valueOf())
      : Number(value);
  };

  const disabledStepGeneral = () => {
    if (objSendForm) {
      return false;
    }
    if (!coverage || !afp || !cuspp) {
      return true;
    }
  };

  // Validación para averageRenum
  const validationAverageProm = () => {
    if (coverage !== "Coberturada Rimac" && !objSendForm) {
      return true;
    }
    if (
      objSendForm &&
      objSendForm.coverage !== "Coberturada Rimac" &&
      !coverage
    ) {
      return true;
    }
    if (
      !coverage &&
      objSendForm &&
      objSendForm.coverage === "Coberturada Rimac"
    ) {
      return false;
    }
    if (coverage !== "Coberturada Rimac") {
      return true;
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////
  // useEffects para lógica específica

  // useEffect para filtrar modalidades según prestación
  useEffect(() => {
    if (prestacionWatch) {
      const saludFind: any = saludData.find((salud: any) => {
        return (
          (prestacionWatch === "IP" && salud.value === "IP") ||
          (prestacionWatch === "IT" && salud.value === "IT") ||
          prestacionWatch === "J" ||
          (prestacionWatch === "S" && salud.value === "S")
        );
      });
      if (saludFind) {
        setSelectSalud(saludFind.name);
        setValue("salud", saludFind.name);
      }
    }
  }, [prestacionWatch, saludData]);

  useEffect(() => {
    if (coverage) {
      setValue("averageRenum", 0);
    }
  }, [coverage]);

  /////////////////////////////////////////////////////////////////////////////////////////////
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Función para enviar el formulario

  const toastMessage = (isSuccess: boolean): void => {
    const toastConfig = {
      position: toast.POSITION.TOP_CENTER,
      className: "toast-modal-success-rem-prom",
    };

    const message = isSuccess
      ? "La cotización se procesó correctamente."
      : "Verifica en el módulo de consultas que la cotización se haya generado correctamente. Si no aparece, vuelve a intentarlo.";

    const showToast = isSuccess ? toast.success : toast.error;
    showToast(message, toastConfig);
  };

  const handleSubmitForm = async (
    request: RVEOFormRequest,
    dataState: RVEODataState
  ) => {
    setLoadingSubmit(true);
    const session: RVEOCurrentUserInfo = await Auth.currentUserInfo();
    const familyName =
      session && session.attributes && session.attributes.family_name
        ? session.attributes.family_name
        : "";
    const givenName =
      session && session.attributes && session.attributes.given_name
        ? session.attributes.given_name
        : "";

    const transformedGrupoFamiliar: FamilyGroup[] =
      request.grupoFamiliar.length > 0
        ? transformDataGF(request.grupoFamiliar)
        : [];
    const transformedModalidades: Modality[] =
      request.modalidades.length > 0 ? transformDataMO(request.modalidades) : [];

    let requestApi: RequestRVEO = {
      asesor: `${givenName} ${familyName}`.trim() || "Asesor no identificado",
      emailAdviser: asesorSel || "",
      cuspp: request.cuspp || "",
      fechaCalculo: "02/07/2024",
      prestacion: request.prestacion ? request.prestacion : "",
      capitalPension: request.capitalPension
        ? String(formatCurrencySummation(request.capitalPension))
        : "",
      fechaDevengue: request.dateDeven
        ? moment(request.dateDeven, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "",
      fechaDevengueOriginal: request.dateDevOrig
        ? moment(request.dateDevOrig, "DD/MM/YYYY").format("YYYY-MM-DD")
        : null,
      gratificacion: request.monthlyPayments === "14 Mensualidades" ? "S" : "N",
      grupoFamiliar: transformedGrupoFamiliar,
      modalidades: transformedModalidades,
      tipoCotizacion: "extra oficiales",
      phone: request.phone || "",
      cellphone: request.cellPhone || "",
      tipoDireccion: request.typeAddress || "",
      salud: request.salud || "",
      afp: request.afp ? Number(request.afp) : 0,
      cic: request.cic || 0,
      remprom: Number(request.averageRenum) || 0,
      address: request.address || "",
      contribucion: request.addContribution || 0,
      bonoReconocimiento: request.recogBonus || 0,
      coverage: request.monthlyPayments || "",
      email: request.email || "",
      asegurado:
        dataState && Object.keys(dataState.assured).length > 0
          ? { 
            ...dataState.assured,
            firstName: dataState.assured.names || '',
            lastName: `${dataState.assured.firstName || ''} ${dataState.assured.lastName || ''}`,
            apellidoPaterno: dataState.assured.firstName || '',
            apellidoMaterno: dataState.assured.lastName || '',
          }
          : {},
      cobertura: request.coverage || "",
    };

    try {
      const response = await submitRVQuotation(requestApi);
      if (
        response &&
        response.payload &&
        response.payload.statusCode === 200
      ) {
        toastMessage(true);
      } else {
        toastMessage(false);
      }
      return response;
    } catch (error) {
      console.error("Error al enviar cotización:", error);
      toastMessage(false);
      throw error;
    } finally {
      setLoadingSubmit(false);
      setTimeout(() => {
        history.push("/simulaciones?tab=RVEO");
      }, 3000);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Return de todas las propiedades y métodos

  return {
    // Referencias
    contentRef,
    containerFormRef,

    // Estados de configuración
    isLoadingConfig,
    steps,
    setSteps,
    city,
    selectAfp,
    selectSalud,
    setSelectSalud,

    // Datos desde Redux
    afpData,
    coverageData,
    serviceData,
    saludData,
    genrerData,
    currencyData,
    modData,
    deferredYearsData,
    tempYearsData,
    guaranteedYearsData,
    relationshipData,
    assured,

    // Navegación
    activeStep,
    setActiveStep,
    stepsForm,
    setStepsForm,
    showTitleActive,
    prevActiveStep,
    nextActiveStep,

    // Formulario principal (Datos Personales)
    handleSubmit,
    register,
    errors,
    watch,
    reset,
    setValue,
    getValues,
    methods,
    coverage,
    prestacionWatch,
    afp,
    cuspp,
    averageRenum,

    // Datos financieros
    methodFinancial,
    handleSubmitFinancial,
    registerFinancial,
    setValueFinancial,
    cic,
    recogBonus,
    addContribution,
    capitalPension,
    dateDeven,
    dateDevOrig,
    saludWatch,

    // Beneficiarios
    parients,
    setParients,
    selectedParient,
    showModalAddBeneficiarie,
    setShowModalAddBeneficiarie,
    deleteParient,
    handleOpenModalAddBeneficiaries,
    handleCloseModalBeneficiarie,
    handleOpenModalEditBeneficiarie,
    handleAddBeneficiarie,
    handleSubmitBeneficiarie,
    registerBeneficiarie,
    errorsBeneficiarie,
    methodsBeneficiarie,
    relationshipBeneficiarie,
    bornDateBeneficiarie,
    saludBeneficiarie,
    genrerBenefiarie,
    setValueBeneficiarie,
    resetBeneficiaries,
    getFilteredRelationshipData,

    // Modalidades
    modalities,
    setModalities,
    selectedModalitie,
    showModalMod,
    setShowModalMod,
    modalitiesOptions,
    setModalitiesOptions,
    deferredYears,
    setDeferredYears,
    deleteModalitie,
    handleOpenModalAddModalities,
    handleCloseModalModalities,
    handleOpenModalEditModalitie,
    handleAddModalitie,
    handleSubmitModalitie,
    registerModalitie,
    errorsModalitie,
    resetModalities,
    modalitiesSelect,
    currencyMod,
    defYearsMod,
    tempYearsMod,
    guarYearsMod,
    setValueModalitie,

    // Funciones auxiliares
    renderName,
    formatCurrency,
    formatCurrencySummation,
    getNumberValue,
    disabledStepGeneral,
    validationAverageProm,

    // Datos del formulario
    objSendForm,
    setObjSendForm,
    handleSubmitForm,
    loadingSubmit,
  };
};
